import { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { getFromStorage, STORAGE_KEYS, Loader } from "./constants";
import { useAppDispatch } from "./hooks/store";
import Pages from "./pages";
import UserDetails from "./pages/users/details";
import UsersForm from "./pages/users/form/usersForm";
import { resetAuth, setCredentials } from "./reducers/authSlice";
import ProtectedRoute from "./components/ProtectedRoute";
import { PERMISSION_MODULES } from "./constants/permissionModules";
import { useLazyGetUserQuery } from "./services/auth";
import PublicProperty from "./pages/public/PublicProperty";
import PublicAgent from "./pages/public/PublicAgent";
import PublicPrivacyPolicy from "./pages/public/PublicPrivacyPolicy";
import PublicTermsConditions from "./pages/public/PublicTermsConditions";
import PublicContactUs from "./pages/public/PublicContactUs";

const PUBLIC_PATHS = [
  "/",
  "/forgot-password",
  "/verify-otp",
  "/reset-password",
  "/changepassword",
  "/privacy-policy",
  "/terms-and-conditions",
  "/contact-us",
];

const Routing = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [getUser] = useLazyGetUserQuery();
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const token = getFromStorage(STORAGE_KEYS.token);
      const pathname = window.location.pathname;

      if (!token) {
        dispatch(resetAuth());
        
        const isPublicDeepLink = [
          "/property/",
          "/agent/",
          "/privacy-policy",
          "/terms-and-conditions",
          "/contact-us",
        ].some((p) => pathname.startsWith(p));
        
        if (!PUBLIC_PATHS.includes(pathname) && !isPublicDeepLink) {
          navigate("/", { replace: true });
        }
        setIsAuthReady(true);
        return;
      }

      dispatch(setCredentials({ token, user: null }));

      try {
        const result = await getUser({}).unwrap();
        if (result?.statusCode === 200) {
          dispatch(setCredentials({ user: result.data, token }));
        }
      } catch {
        setIsAuthReady(true);
        return;
      }

      if (pathname === "/") {
        navigate("/dashboard", { replace: true });
      }

      setIsAuthReady(true);
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthReady) {
    return <Loader isLoad={true} />;
  }

  return (
    <Routes>
      <Route path="/" element={<Pages.LoginPage />} />
      <Route path="/forgot-password" element={<Pages.ForgotPasswordPage />} />
      <Route path="/verify-otp" element={<Pages.VerifyOtp />} />
      <Route path="/reset-password" element={<Pages.ResetPassword />} />
      <Route path="/changepassword" element={<Pages.ChangePassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.DASHBOARD}>
            <Pages.DashBoardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/profile" element={<Pages.Profile />} />
      <Route
        path="/manageusers"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_USERS}>
            <Pages.ManageUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manageusers/userdetails/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_USERS}>
            <UserDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manageusers/usersform/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_USERS}>
            <UsersForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/faq"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.FAQS}>
            <Pages.Faq />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cms"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.SETTINGS}>
            <Pages.ManageCms />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-properties"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROPERTIES}>
            <Pages.ManageProperties />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-properties/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROPERTIES}>
            <Pages.AddProperty />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-properties/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROPERTIES}>
            <Pages.AddProperty />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-payments"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PAYMENTS}>
            <Pages.ManagePayments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-payments/details/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PAYMENTS}>
            <Pages.PaymentDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute
            module={PERMISSION_MODULES.ANALYTICS}
            alternateModules={[PERMISSION_MODULES.DASHBOARD]}
          >
            <Pages.Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subscription"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS}>
            <Pages.ManageSubscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subscription/purchases"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS}>
            <Pages.PurchasedSubscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subscription/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS}>
            <Pages.AddSubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subscription/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS}>
            <Pages.AddSubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subscription/edit"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS}>
            <Pages.EditSubscriptionPlan />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-notifications"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_NOTIFICATIONS}>
            <Pages.ManageNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-notifications/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_NOTIFICATIONS}>
            <Pages.AddNotification />
          </ProtectedRoute>
        }
      />
      <Route
        path="/recieved-notifications"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_NOTIFICATIONS}>
            <Pages.RecievedNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-properties/details/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROPERTIES}>
            <Pages.PropertyDetails />
          </ProtectedRoute>
        }
      />
      <Route path="/manage-labels" element={
        <ProtectedRoute adminOnly>
          <Pages.ManageLabels />
        </ProtectedRoute>
      } />
      <Route
        path="/manage-agents"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AGENTS}>
            <Pages.ManageAgents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-agents/details/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AGENTS}>
            <Pages.AgentDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-agents/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AGENTS}>
            <Pages.AgentsForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-categories"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.ManageCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-categories/sub-categories/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.SubCategory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-categories/sub-categories/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.AddSubCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-categories/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.AddCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-categories/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.AddCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit-categories/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_CATEGORIES}>
            <Pages.EditCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subAdmin"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUB_ADMIN}>
            <Pages.ManageSubAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subAdmin/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUB_ADMIN}>
            <Pages.AddSubAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-subAdmin/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_SUB_ADMIN}>
            <Pages.AddSubAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-tutorials"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.PROMOTIONAL_TUTORIALS}>
            <Pages.ManageTutorial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-tutorials/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.PROMOTIONAL_TUTORIALS}>
            <Pages.AddTutorial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-tutorials/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.PROMOTIONAL_TUTORIALS}>
            <Pages.AddTutorial />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.ManageAreas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas/sub-areas/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.ManageSubAreas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas/sub-areas/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.AddSubAreas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas/sub-areas/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.AddSubAreas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.AddAreas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-areas/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_AREAS}>
            <Pages.AddAreas />
          </ProtectedRoute>
        }
      />
      <Route path="/permission-denied" element={<Pages.PermissionDenied />} />
      <Route
        path="/manage-discount"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE}>
            <Pages.ManageDiscount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-discount/add"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE}>
            <Pages.AddDiscount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-discount/edit/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.MANAGE_PROMOTIONAL_CODE}>
            <Pages.AddDiscount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/refund-requests"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.REFUND_REQUESTS}>
            <Pages.RefundRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/refund-requests/details/:id"
        element={
          <ProtectedRoute module={PERMISSION_MODULES.REFUND_REQUESTS}>
            <Pages.RefundDetails />
          </ProtectedRoute>
        }
      />
      <Route path="/property/:id" element={<PublicProperty />} />
      <Route path="/agent/:id" element={<PublicAgent />} />
      <Route path="/privacy-policy" element={<PublicPrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<PublicTermsConditions />} />
      <Route path="/contact-us" element={<PublicContactUs />} />
    </Routes>
  );
};

export default Routing;
